import React, { useEffect, useState } from 'react';
import { Autocomplete, Box, Paper, Typography, Tabs, Tab, Stack, TextField, Button, Table, TableHead, TableBody, TableRow, TableCell, TablePagination, Avatar, Chip, Alert, Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress } from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import GroupsIcon from '@mui/icons-material/Groups';
import LinkIcon from '@mui/icons-material/Link';
import AssessmentIcon from '@mui/icons-material/Assessment';
import CampaignIcon from '@mui/icons-material/Campaign';
import { API_URL } from './config';
import { getAuthHeaders } from './commonfunctions/useAuthHelpers';
const ORANGE = '#F07828', ORANGE2 = '#ed7f35ff', BLUE = '#1565C0', BG = '#F2F2F2', BORDER = 'rgba(25,118,210,0.20)';
const surface = { borderRadius: 2, backgroundColor: 'white', border: `1px solid ${BORDER}` };
const section = { ...surface, p: 2, position: 'relative', overflow: 'hidden', '&:before': { content: '""', position: 'absolute', left: 0, top: 0, bottom: 0, width: 6, background: `linear-gradient(180deg,${ORANGE},${ORANGE2})` } };
const input = { '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: BORDER }, '&:hover fieldset': { borderColor: 'rgba(25,118,210,0.45)' }, '&.Mui-focused fieldset': { borderColor: 'rgba(240,120,40,0.80)' } } };
const primary = { borderRadius: 2, textTransform: 'none', fontWeight: 900, backgroundColor: ORANGE, '&:hover': { backgroundColor: ORANGE2 } };
const outlined = { borderRadius: 2, textTransform: 'none', fontWeight: 800, borderColor: 'rgba(25,118,210,0.35)', color: BLUE, '&:hover': { borderColor: 'rgba(25,118,210,0.55)', backgroundColor: 'rgba(25,118,210,0.04)' } };
const tabStyle = { px: 1, borderBottom: `1px solid ${BORDER}`, minHeight: 48, '& .MuiTab-root': { textTransform: 'none', fontWeight: 800, minHeight: 48 }, '& .Mui-selected': { color: `${ORANGE} !important` }, '& .MuiTabs-indicator': { backgroundColor: ORANGE, height: 3 } };
// Match StudentAlumniMentors: photo paths returned by the API are relative to API_URL.
const photo = (r) => {
  const url = r?.PhotoURL || r?.ProfilePhotoURL || r?.ProfilePhotoUrl || r?.ProfilePhoto;
  if (!url || typeof url !== 'string')
    return undefined;
  const value = url.trim();
  if (!value)
    return undefined;
  if (/^https?:\/\//i.test(value))
    return value;
  if (value.startsWith('//'))
    return `${window.location.protocol}${value}`;
  const base = String(API_URL || '').replace(/\/$/, '');
  return `${base}/${value.replace(/^\/+/, '')}`;
};
const tabs = [['Dashboard', <DashboardIcon />], ['Alumni Directory', <GroupsIcon />], ['Student Account Linking', <LinkIcon />], ['Registration Links & Analysis', <AssessmentIcon />], ['WhatsApp Campaigns', <CampaignIcon />]];
const FILTERS = [['batch', 'Batch'], ['subCampus', 'Sub Campus'], ['degree', 'Degree'], ['branch', 'Branch'], ['mentor', 'Mentor'], ['hiring', 'Hiring']];
export default function AlumniAdminPage({ apiBase = `${API_URL}/api/alumni/admin`, publicOrigin = window.location.origin }) {
  const [tab, setTab] = useState(0), [summary, setSummary] = useState({}), [rows, setRows] = useState([]), [total, setTotal] = useState(0);
  const [page, setPage] = useState(0), [size, setSize] = useState(25), [q, setQ] = useState(''), [filters, setFilters] = useState({});
  const [options, setOptions] = useState({}), [links, setLinks] = useState([]), [label, setLabel] = useState(''), [year, setYear] = useState('');
  const [preview, setPreview] = useState(null), [err, setErr] = useState(''), [success, setSuccess] = useState('');
  const [selected, setSelected] = useState(null), [studentId, setStudentId] = useState(''), [busy, setBusy] = useState(false), [studentOptions, setStudentOptions] = useState([]), [searching, setSearching] = useState(false);
  const request = async (path, opts = {}) => {
    const response = await fetch(`${apiBase}${path}`, { credentials: 'include', ...opts, headers: { ...getAuthHeaders(), ...(opts.body ? { 'Content-Type': 'application/json' } : {}), ...(opts.headers || {}) } }); const data = await response.json(); if (!response.ok)
      throw Error(data.error || 'Request failed'); return data;
  };
  const load = async () => {
    try {
      setErr('');
      const params = new URLSearchParams({ page: page + 1, size, q, ...Object.fromEntries(Object.entries(filters).filter(([, v]) => v)) });
      const [s, p, l] = await Promise.all([request('/summary'), request('/profiles?' + params), request('/group-links')]);
      setSummary(s);
      setRows(p.rows || []);
      setTotal(p.total ?? p.rows?.length ?? 0);
      setLinks(l.rows || []);
    }
    catch (e) {
      setErr(e.message);
    }
  };
  useEffect(() => { load(); }, [page, size, q, JSON.stringify(filters)]);
  useEffect(() => {
    let active = true; request('/filter-options').then(d => {
      if (active)
        setOptions(d);
    }).catch(e => {
      if (active)
        setErr(e.message);
    }); return () => { active = false; };
  }, []);
  const act = async (path, payload) => {
    try {
      setBusy(true);
      setErr('');
      const result = await request(path, { method: 'POST', body: JSON.stringify(payload) });
      setSuccess('Saved successfully');
      await load();
      return result;
    }
    catch (e) {
      setErr(e.message);
      return null;
    }
    finally {
      setBusy(false);
    }
  };
  useEffect(() => {
    if (!selected || studentId.trim().length < 2) {
      setStudentOptions([]);
      setSearching(false);
      return;
    } let active = true; setSearching(true); const t = setTimeout(() => request('/student-search?q=' + encodeURIComponent(studentId.trim())).then(d => {
      if (active)
        setStudentOptions(d.rows || []);
    }).catch(e => {
      if (active)
        setErr(e.message);
    }).finally(() => {
      if (active)
        setSearching(false);
    }), 300); return () => { active = false; clearTimeout(t); };
  }, [studentId, selected]);
  const changeFilter = (key, value) => { setFilters(f => ({ ...f, [key]: value || '' })); setPage(0); };
  const renderFilters = () => <Paper elevation={0} sx={section}>
    <Typography fontWeight={900} color={BLUE} mb={1.5}>
      Filters
    </Typography>
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2,minmax(180px,1fr))', md: 'repeat(4,minmax(180px,1fr))' }, gap: 1.5 }}>
      <TextField size="small" label="Search name, Student ID, city, company, batch, academic details" value={q} onChange={e => { setQ(e.target.value); setPage(0); }} sx={{ ...input, gridColumn: { md: 'span 2' } }} />
      {FILTERS.map(([key, title]) => <Autocomplete key={key} size="small" options={key === 'mentor' || key === 'hiring' ? ['Yes', 'No'] : (options[key] || [])} value={filters[key] ? key === 'mentor' || key === 'hiring' ? (filters[key] === 'yes' ? 'Yes' : 'No') : filters[key] : null} onChange={(_, v) => changeFilter(key, key === 'mentor' || key === 'hiring' ? v?.toLowerCase() : v)} renderInput={params => <TextField {...params} label={title} sx={input} />} />)}
      <Button variant="outlined" sx={outlined} onClick={() => { setQ(''); setFilters({}); setPage(0); }}>
        Clear Filters
      </Button>
    </Box>
  </Paper>;
  const showCell = (r, c) => {
    if (c === 'Photo')
      return <Avatar src={photo(r)} imgProps={{ referrerPolicy: 'no-referrer' }} sx={{ width: 48, height: 48, border: `2px solid ${BORDER}`, bgcolor: 'rgba(25,118,210,0.12)', color: BLUE, fontWeight: 800 }}>
        {r.FullName?.[0]}
      </Avatar>;
    if (c === 'Actions')
      return r.IsLinked ? <Chip size="small" color="success" label={`Linked: ${r.StudentID || ''}`} /> : <Button variant="outlined" size="small" sx={outlined} onClick={() => { setSelected(r); setStudentId(r.StudentID || ''); setStudentOptions([]); }}>
        Link student
      </Button>;
    if (c === 'Mentoring')
      return <Chip size="small" label={r.IsMentor ? 'Available' : 'No'} color={r.IsMentor ? 'success' : 'default'} />;
    if (c === 'Hiring')
      return <Chip size="small" label={r.ReadyToHire ? 'Hiring' : 'No'} color={r.ReadyToHire ? 'success' : 'default'} />;
    if (c === 'StudentID')
      return r.StudentID ? <Typography fontWeight={800} color={BLUE}>
        {r.StudentID}
      </Typography> : '—';
    return r[c] ?? '—';
  };
  const table = cols => <Paper elevation={0} sx={{ ...surface, overflow: 'hidden' }}>
    <Box sx={{ overflowX: 'auto' }}>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ backgroundColor: 'rgba(25,118,210,0.95)', '& th': { color: 'white', fontWeight: 900, whiteSpace: 'nowrap' } }}>
            <TableCell>
              #
            </TableCell>
            {cols.map(c => <TableCell key={c}>
              {c}
            </TableCell>)}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r, i) => <TableRow key={r.AlumniID || i} sx={{ backgroundColor: i % 2 === 0 ? 'rgba(25,118,210,0.06)' : 'white', '&:hover': { backgroundColor: 'rgba(25,118,210,0.10)' } }}>
            <TableCell>
              <Box sx={{ width: 38, height: 38, borderRadius: '50%', display: 'grid', placeItems: 'center', fontWeight: 900, color: '#1976D2', backgroundColor: 'rgba(25,118,210,0.06)', border: '2px solid rgba(25,118,210,0.20)' }}>
                {page * size + i + 1}
              </Box>
            </TableCell>
            {cols.map(c => <TableCell key={c}>
              {showCell(r, c)}
            </TableCell>)}
          </TableRow>)}
          {!rows.length && <TableRow>
            <TableCell colSpan={cols.length + 1} align="center" sx={{ py: 4 }}>
              No matching records.
            </TableCell>
          </TableRow>}
        </TableBody>
      </Table>
    </Box>
    <TablePagination component="div" count={total} page={page} rowsPerPage={size} onPageChange={(_, p) => setPage(p)} onRowsPerPageChange={e => { setSize(Number(e.target.value)); setPage(0); }} rowsPerPageOptions={[10, 25, 50, 100]} showFirstButton showLastButton />
  </Paper>;
  return <Box sx={{ p: 2, backgroundColor: BG, minHeight: '100vh' }}>
    <Paper elevation={5} sx={{ mx: 'auto', borderRadius: 2, overflow: 'hidden', border: '1px solid rgba(25,118,210,0.15)' }}>
      <Box sx={{ px: 2, py: 1.5, color: 'white', background: `linear-gradient(90deg,${ORANGE} 0%,${ORANGE2} 100%)` }}>
        <Typography fontWeight={900} fontSize="1.1rem">
          Alumni Administration
        </Typography>
        <Typography sx={{ opacity: .9, fontSize: '.85rem' }}>
          Directory · student account linking · mentoring · hiring · outreach
        </Typography>
      </Box>
      <Box sx={{ p: 2, backgroundColor: BG }}>
        {err && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErr('')}>
          {err}
        </Alert>}
        {success && <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 2 }}>
          {success}
        </Alert>}
        <Paper elevation={0} sx={{ ...surface, overflow: 'hidden' }}>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile sx={tabStyle}>
            {tabs.map(([name, icon]) => <Tab key={name} label={name} icon={icon} iconPosition="start" />)}
          </Tabs>
          <Box sx={{ p: 2 }}>
            {tab === 0 && <Stack spacing={2}>
              <Stack direction="row" flexWrap="wrap" gap={2}>
                {[['Registered', summary.total], ['Recently confirmed', summary.fresh], ['Confirmation due', summary.due], ['WhatsApp enabled', summary.withPhone], ['Mentors', summary.mentors], ['Ready to hire', summary.hiring], ['Linked accounts', summary.linked]].map(([k, v]) => <Paper key={k} elevation={0} sx={{ ...section, flex: '1 1 160px' }}>
                  <Typography color={BLUE} fontWeight={800}>
                    {k}
                  </Typography>
                  <Typography variant="h4" fontWeight={900}>
                    {Number(v || 0).toLocaleString()}
                  </Typography>
                </Paper>)}
              </Stack>
              <Paper elevation={0} sx={section}>
                <Typography fontWeight={900} color={BLUE}>
                  Mentorship & hiring overview
                </Typography>
                <Typography variant="body2">
                  Use the directory filters to identify available mentors, employers and alumni requiring account linking. Counts are campus-scoped.
                </Typography>
              </Paper>
            </Stack>}
            {(tab === 1 || tab === 2) && <Stack spacing={2}>
              {renderFilters()}
              {table(tab === 1 ? ['Photo', 'FullName', 'StudentID', 'GraduationYear', 'CurrentCity', 'CurrentCompany', 'Mentoring', 'Hiring'] : ['Photo', 'FullName', 'StudentID', 'GraduationYear', 'Actions'])}
            </Stack>}
            {tab === 3 && <Stack spacing={2}>
              <Paper elevation={0} sx={section}>
                <Typography color={BLUE} fontWeight={900} mb={2}>
                  Registration links
                </Typography>
                <Stack direction="row" gap={1} flexWrap="wrap">
                  <TextField size="small" label="Group label" value={label} sx={input} onChange={e => setLabel(e.target.value)} />
                  <TextField size="small" type="number" label="Batch year" sx={input} value={year} onChange={e => setYear(e.target.value)} />
                  <Button variant="contained" disabled={!label.trim() || busy} sx={primary} onClick={async () => {
                    if (await act('/group-links', { label, batchYear: year || null }))
                      setLabel('');
                  }}>
                    Create link
                  </Button>
                </Stack>
              </Paper>
              <Paper elevation={0} sx={section}>
                <Typography color={BLUE} fontWeight={900}>
                  Link performance
                </Typography>
                <Typography variant="body2">
                  Registration attribution requires link analytics in the backend. Links and batch targeting are shown below.
                </Typography>
                {links.map(l => <Box key={l.LinkID} sx={{ py: 1, borderBottom: `1px solid ${BORDER}` }}>
                  <b>
                    {l.Label}
                  </b>
                  ·
                  {l.BatchYear || 'All batches'}
                  ·
                  <a href={`${publicOrigin}/alumni/register/${l.Slug}`} target="_blank" rel="noreferrer">
                    Open registration link
                  </a>
                </Box>)}
              </Paper>
            </Stack>}
            {tab === 4 && <Paper elevation={0} sx={section}>
              <Typography color={BLUE} fontWeight={900}>
                WhatsApp profile confirmation
              </Typography>
              <Typography sx={{ my: 2 }}>
                Send only to verified numbers with active consent and without opt-outs. Actual sending requires your configured worker and approved template.
              </Typography>
              <Button variant="outlined" sx={outlined} onClick={async () => {
                try {
                  setPreview((await request('/campaigns/preview', { method: 'POST', body: '{}' })).eligible);
                }
                catch (e) {
                  setErr(e.message);
                }
              }}>
                Preview eligible recipients
              </Button>
              {preview !== null && <Stack direction="row" alignItems="center" gap={2} mt={2}>
                <Typography>
                  {preview}
                  eligible alumni
                </Typography>
                <Button disabled={!preview || busy} variant="contained" sx={primary} onClick={async () => {
                  if (window.confirm(`Queue reminders for ${preview} alumni?`)) {
                    await act('/campaigns', { name: 'Six-month confirmation' });
                    setPreview(null);
                  }
                }}>
                  Queue campaign
                </Button>
              </Stack>}
            </Paper>}
          </Box>
        </Paper>
      </Box>
    </Paper>
    <Dialog open={!!selected} onClose={() => setSelected(null)} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden', border: `1px solid ${BORDER}` } }}>
      <DialogTitle sx={{ px: 3, py: 2, color: 'white', background: `linear-gradient(90deg,${ORANGE},${ORANGE2})`, fontWeight: 900 }}>
        Connect alumni to student account
      </DialogTitle>
      <DialogContent sx={{ backgroundColor: BG, p: 3 }}>
        <Paper elevation={0} sx={{ ...section, mt: 1 }}>
          <Typography mb={2}>
            {selected?.FullName}
            — verify identity before linking.
          </Typography>
          <Autocomplete freeSolo options={studentOptions} filterOptions={x => x} getOptionLabel={option => typeof option === 'string' ? option : String(option.StudentID || '')} inputValue={studentId} onInputChange={(_, v, reason) => {
            if (reason === 'input' || reason === 'clear')
              setStudentId(v);
          }} onChange={(_, v) => {
            if (v)
              setStudentId(typeof v === 'string' ? v : v.StudentID);
          }} loading={searching} noOptionsText={studentId.trim().length < 2 ? 'Enter at least 2 characters' : 'No student IDs found'} renderInput={params => <TextField {...params} label="Search and select Student ID" size="small" fullWidth sx={input} helperText="Search by Student ID. Internal StudentKey is never displayed." InputProps={{
            ...params.InputProps, endAdornment: <>
              {searching ? <CircularProgress color="inherit" size={18} /> : null}
              {params.InputProps.endAdornment}
            </>
          }} />} />
        </Paper>
      </DialogContent>
      <DialogActions sx={{ p: 2, backgroundColor: BG }}>
        <Button sx={outlined} variant="outlined" onClick={() => setSelected(null)}>
          Cancel
        </Button>
        <Button sx={primary} variant="contained" disabled={busy || !studentId.trim()} onClick={async () => {
          const result = await act(`/profiles/${selected.AlumniID}/link-student`, { studentId: studentId.trim() }); if (result) {
            setSuccess(`Linked ${selected.FullName} to Student ID ${result.studentId || studentId.trim()}`);
            setSelected(null);
          }
        }}>
          Verify & Link
        </Button>
      </DialogActions>
    </Dialog>
  </Box>;
}
